import React, { useState, useEffect } from 'react';
import { Mail, Search, RefreshCw, Send, Inbox, AlertTriangle, Check, X,
         CornerUpLeft, Settings, ChevronRight } from 'lucide-react';
import { useRafraichissementAuto } from '../rafraichissementAuto';
import { useBlocageDefilement } from '../blocageDefilement';

/* ---------------------------------------------------------------------------
   Onglet "Mail ES-PROTECT" : tout ce qui part vers les organismes et tout ce
   qui en revient, au même endroit.

   Rien ne part d'ici automatiquement : les envois se déclenchent depuis les
   dossiers, par décision d'un administrateur. Cet écran sert à suivre, à lire
   les réponses et à répondre à la main quand il le faut.
   --------------------------------------------------------------------------- */

const API = process.env.REACT_APP_API_URL || '/api';
const jetonEntete = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const dateHeure = (v) => (v ? new Date(String(v).replace(' ', 'T')).toLocaleString('fr-FR',
  { timeZone: 'Europe/Paris', day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit' }) : '—');
const nombre = (v) => Number(v || 0).toLocaleString('fr-FR');
const champ = { padding: '9px 12px', border: '1px solid #dfe3ea', borderRadius: 6,
                fontSize: 13.5, color: '#263238' };

/* ─────────────────────────── lecture d'un message ─────────────────────────── */
function Message({ courriel, onFermer, onRepondu }) {
  const [reponse, setReponse] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState(null);
  const recu = courriel.sens === 'recu';
  useBlocageDefilement(true);

  const repondre = async () => {
    if (!reponse.trim()) return;
    setEnvoi(true); setErreur(null);
    try {
      const r = await fetch(`${API}/emandat/courriels/${courriel.id}/repondre`, {
        method: 'POST', headers: { ...jetonEntete(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ texte: reponse })
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `Erreur ${r.status}`);
      onRepondu(`Réponse envoyée à ${j.destinataire}`);
      onFermer();
    } catch (e) { setErreur(e.message); }
    setEnvoi(false);
  };

  return (
    <div onClick={onFermer}
         style={{ position: 'fixed', inset: 0, background: 'rgba(15,20,35,.55)', zIndex: 1000,
                  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                  padding: '4vh 16px', overflowY: 'auto' }}>
      <div onClick={(e) => e.stopPropagation()}
           style={{ background: '#fff', borderRadius: 10, width: 'min(820px,100%)',
                    boxShadow: '0 20px 60px rgba(10,15,30,.35)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '18px 22px',
                      borderBottom: '1px solid #eceff1' }}>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 17, color: '#263238' }}>{courriel.sujet}</h3>
            <div style={{ fontSize: 12.5, color: '#78909c' }}>
              <b>{recu ? 'De' : 'À'}</b> {recu ? courriel.de : courriel.destinataire}
              {' · '}{dateHeure(courriel.cree_le)}
              {courriel.organisme && <> · {courriel.organisme}</>}
              {courriel.prenom && <> · dossier de {courriel.prenom} {courriel.nom_client}</>}
            </div>
          </div>
          <button onClick={onFermer} style={{ background: 'none', border: 'none', cursor: 'pointer',
                                              color: '#90a4ae' }}><X size={20} /></button>
        </div>

        <div style={{ padding: '16px 22px' }}>
          {courriel.statut === 'echec' && (
            <div style={{ background: '#ffebee', border: '1px solid #ffcdd2', borderRadius: 8,
                          padding: '10px 13px', marginBottom: 12, fontSize: 13, color: '#b71c1c' }}>
              <b>Ce message n’est pas parti.</b> {courriel.erreur}
            </div>
          )}
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0,
                        fontFamily: 'inherit', fontSize: 13.5, lineHeight: 1.6, color: '#37474f',
                        background: '#f8f9fc', border: '1px solid #eceff1', borderRadius: 8,
                        padding: '14px 16px', maxHeight: 340, overflowY: 'auto' }}>
{courriel.texte || '(message vide)'}
          </pre>

          {courriel.pieces_jointes && courriel.pieces_jointes !== '[]' && (
            <p style={{ margin: '10px 0 0', fontSize: 12.5, color: '#78909c' }}>
              Pièces jointes : {(() => {
                try { return JSON.parse(courriel.pieces_jointes).map((p) => p.nom).join(', '); }
                catch (e) { return courriel.pieces_jointes; }
              })()}
            </p>
          )}

          {recu && (
            <div style={{ marginTop: 18 }}>
              <h4 style={{ margin: '0 0 6px', fontSize: 12.5, textTransform: 'uppercase',
                           letterSpacing: '.05em', color: '#667eea' }}>Répondre</h4>
              <textarea value={reponse} onChange={(e) => setReponse(e.target.value)} rows={7}
                        placeholder="Votre réponse…"
                        style={{ ...champ, width: '100%', fontFamily: 'inherit', lineHeight: 1.5 }} />
              {erreur && <p style={{ color: '#b71c1c', fontSize: 13, margin: '6px 0 0' }}>{erreur}</p>}
              <button onClick={repondre} disabled={envoi || !reponse.trim()}
                      style={{ ...champ, marginTop: 8, border: 'none',
                               background: reponse.trim() ? '#000091' : '#b0bec5', color: '#fff',
                               fontWeight: 600, cursor: reponse.trim() ? 'pointer' : 'not-allowed',
                               display: 'flex', alignItems: 'center', gap: 6 }}>
                <Send size={14} /> {envoi ? 'Envoi…' : `Répondre à ${courriel.de}`}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────── l'onglet ──────────────────────────────── */
export default function MailEsprotect() {
  const [etat, setEtat] = useState(null);
  const [donnees, setDonnees] = useState({ lignes: [], total: 0, pages: 1 });
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [message, setMessage] = useState(null);
  const [sens, setSens] = useState('');
  const [recherche, setRecherche] = useState('');
  const [recherchee, setRecherchee] = useState('');
  const [page, setPage] = useState(1);
  const [ouvert, setOuvert] = useState(null);
  const [enCours, setEnCours] = useState(null);

  const charger = async ({ silencieux = false } = {}) => {
    if (!silencieux) setChargement(true);
    try {
      const p = new URLSearchParams({ page: String(page), taille: '50' });
      if (sens) p.set('sens', sens);
      if (recherchee) p.set('q', recherchee);
      const [r1, r2] = await Promise.all([
        fetch(`${API}/emandat/courriels?${p}`, { headers: jetonEntete() }),
        fetch(`${API}/emandat/messagerie/etat`, { headers: jetonEntete() })
      ]);
      const j1 = await r1.json(); const j2 = await r2.json();
      if (!r1.ok) throw new Error(j1.error || `Erreur ${r1.status}`);
      setDonnees(j1); setEtat(j2); setErreur(null);
    } catch (e) { setErreur(e.message); }
    if (!silencieux) setChargement(false);
  };
  useEffect(() => { charger(); /* eslint-disable-next-line */ }, [page, sens, recherchee]);
  useRafraichissementAuto(() => charger({ silencieux: true }), { secondes: 60, actif: !ouvert });
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); setRecherchee(recherche.trim()); }, 400);
    return () => clearTimeout(t);
  }, [recherche]);

  const relever = async () => {
    setEnCours('releve');
    try {
      const r = await fetch(`${API}/emandat/courriels/relever`, { method: 'POST', headers: jetonEntete() });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `Erreur ${r.status}`);
      setMessage({ type: 'ok', texte: j.nouveaux
        ? `${j.nouveaux} nouveau${j.nouveaux > 1 ? 'x' : ''} message${j.nouveaux > 1 ? 's' : ''}`
          + (j.rattaches ? `, dont ${j.rattaches} rattaché${j.rattaches > 1 ? 's' : ''} à un dossier` : '')
        : 'Aucun nouveau message' });
      await charger({ silencieux: true });
    } catch (e) { setMessage({ type: 'ko', texte: e.message }); }
    setEnCours(null);
  };

  const verifier = async () => {
    setEnCours('verif');
    try {
      const r = await fetch(`${API}/emandat/messagerie/verifier`, { method: 'POST', headers: jetonEntete() });
      const j = await r.json();
      setMessage({ type: j.smtp === 'ok' && String(j.imap || '').startsWith('ok') ? 'ok' : 'ko',
        texte: j.configuree === false ? j.message
          : `Envoi : ${j.smtp} · Réception : ${j.imap}` });
    } catch (e) { setMessage({ type: 'ko', texte: e.message }); }
    setEnCours(null);
  };

  const ouvrir = async (c) => {
    setOuvert(c);
    if (c.sens === 'recu' && !c.lu) {
      await fetch(`${API}/emandat/courriels/${c.id}/lu`, { method: 'POST', headers: jetonEntete() });
      charger({ silencieux: true });
    }
  };

  const e = etat || {};
  const co = e.compteurs || {};

  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: '22px 24px 28px',
                  boxShadow: '0 4px 18px rgba(0,0,0,.08)' }}>
      <h2 style={{ display: 'flex', alignItems: 'center', gap: 9, margin: '0 0 4px',
                   fontSize: 20, color: '#263238' }}>
        <Mail size={20} color="#000091" /> Mail ES-PROTECT
      </h2>
      <p style={{ margin: '0 0 18px', fontSize: 12.5, color: '#90a4ae' }}>
        Tout ce qui part vers les organismes et tout ce qui en revient. Les envois se déclenchent
        depuis les dossiers, jamais d’ici : <b>rien ne part sans votre validation</b>.
      </p>

      {/* etat de la boite */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 11, flexWrap: 'wrap',
                    background: e.configuree ? '#f1f8f2' : '#fff8e1',
                    border: `1px solid ${e.configuree ? '#c8e6c9' : '#ffe0b2'}`,
                    borderRadius: 8, padding: '12px 15px', marginBottom: 16 }}>
        {e.configuree ? <Check size={18} color="#2e7d32" style={{ marginTop: 1 }} />
                      : <AlertTriangle size={18} color="#8d6e00" style={{ marginTop: 1 }} />}
        <div style={{ flex: 1, minWidth: 260 }}>
          <b style={{ fontSize: 13.5, color: e.configuree ? '#1b5e20' : '#8d6e00' }}>
            {e.configuree ? `Boîte connectée : ${e.expediteur}`
                          : 'Boîte pas encore configurée'}
          </b>
          <p style={{ margin: '3px 0 0', fontSize: 12.5, color: '#546e7a' }}>
            {e.configuree
              ? <>Envoi par {e.serveur_envoi} · réception par {e.serveur_reception}.</>
              : <>Les identifiants de la boîte Hostinger ne sont pas renseignés : aucun message ne
                  peut partir ni être relevé. Tout le reste est prêt et fonctionnera dès qu’ils
                  seront en place.</>}
          </p>
        </div>
        <button onClick={verifier} disabled={enCours === 'verif'}
                style={{ ...champ, background: '#fff', cursor: 'pointer', display: 'flex',
                         alignItems: 'center', gap: 6 }}>
          <Settings size={14} /> {enCours === 'verif' ? 'Test…' : 'Tester la connexion'}
        </button>
        <button onClick={relever} disabled={enCours === 'releve' || !e.configuree}
                style={{ ...champ, border: 'none', background: e.configuree ? '#000091' : '#b0bec5',
                         color: '#fff', fontWeight: 600,
                         cursor: e.configuree ? 'pointer' : 'not-allowed',
                         display: 'flex', alignItems: 'center', gap: 6 }}>
          <Inbox size={14} /> {enCours === 'releve' ? 'Relève…' : 'Relever la boîte'}
        </button>
      </div>

      {message && (
        <div style={{ padding: '11px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13,
                      display: 'flex', alignItems: 'center', gap: 8,
                      background: message.type === 'ok' ? '#e8f5e9' : '#ffebee',
                      color: message.type === 'ok' ? '#1b5e20' : '#b71c1c' }}>
          {message.type === 'ok' ? <Check size={16} /> : <AlertTriangle size={16} />}
          <span style={{ flex: 1 }}>{message.texte}</span>
          <button onClick={() => setMessage(null)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>
            <X size={15} />
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        {[['Envoyés', co.envoyes, '#14448f', 'envoye'], ['Reçus', co.recus, '#1b5e20', 'recu'],
          ['Non lus', co.non_lus, '#e65100', 'recu'], ['Échecs', co.echecs, '#b71c1c', '']]
          .map(([t, v, c, s2]) => (
          <button key={t} onClick={() => { setPage(1); setSens(sens === s2 ? '' : s2); }}
                  style={{ border: `1px solid ${sens && sens === s2 ? c : '#eceff1'}`,
                           borderRadius: 8, padding: '10px 16px', minWidth: 104, cursor: 'pointer',
                           textAlign: 'left', background: sens && sens === s2 ? `${c}0f` : '#fff' }}>
            <div style={{ fontSize: 19, fontWeight: 700, color: c }}>{nombre(v)}</div>
            <div style={{ fontSize: 11.5, color: '#90a4ae' }}>{t}</div>
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ position: 'relative', flex: '1 1 240px' }}>
          <Search size={15} style={{ position: 'absolute', left: 11, top: 11, color: '#b0bec5' }} />
          <input value={recherche} onChange={(ev) => setRecherche(ev.target.value)}
                 placeholder="Objet, expéditeur, contenu…"
                 style={{ ...champ, width: '100%', paddingLeft: 34 }} />
        </div>
        <select value={sens} onChange={(ev) => { setPage(1); setSens(ev.target.value); }} style={champ}>
          <option value="">Tous les messages</option>
          <option value="envoye">Envoyés</option>
          <option value="recu">Reçus</option>
        </select>
        <button onClick={() => charger({ silencieux: true })}
                style={{ ...champ, background: '#fff', cursor: 'pointer', display: 'flex',
                         alignItems: 'center', gap: 6 }}>
          <RefreshCw size={14} /> Actualiser
        </button>
      </div>

      {erreur && <div style={{ background: '#ffebee', color: '#b71c1c', padding: '12px 16px',
                               borderRadius: 6, marginBottom: 14 }}>{erreur}</div>}

      {chargement ? <p style={{ color: '#90a4ae' }}>Chargement…</p> : donnees.lignes.length === 0 ? (
        <p style={{ color: '#90a4ae', fontSize: 13.5 }}>
          Aucun message pour le moment. Les demandes envoyées depuis les dossiers apparaîtront ici,
          ainsi que les réponses des organismes.
        </p>
      ) : (
        <div style={{ border: '1px solid #eceff1', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13,
                          tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '11%' }} /><col style={{ width: '13%' }} />
              <col style={{ width: '21%' }} /><col style={{ width: '32%' }} />
              <col style={{ width: '13%' }} /><col style={{ width: '10%' }} />
            </colgroup>
            <thead>
              <tr style={{ background: '#f7f8fb', textAlign: 'left' }}>
                {['Sens', 'Date', 'Interlocuteur', 'Objet', 'Dossier', ''].map((t, i) => (
                  <th key={i} style={{ padding: '10px 12px', fontSize: 11.5, color: '#78909c',
                                       textTransform: 'uppercase', letterSpacing: '.04em',
                                       textAlign: i === 5 ? 'right' : 'left' }}>{t}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {donnees.lignes.map((c, i) => {
                const recu = c.sens === 'recu';
                const tronque = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
                const nonLu = recu && !c.lu;
                return (
                  <tr key={c.id} onClick={() => ouvrir(c)}
                      style={{ cursor: 'pointer', background: i % 2 ? '#fcfcfd' : '#fff',
                               borderBottom: '1px solid #f3f5f8',
                               fontWeight: nonLu ? 600 : 400 }}
                      onMouseEnter={(ev) => { ev.currentTarget.style.background = '#f0f1fb'; }}
                      onMouseLeave={(ev) => { ev.currentTarget.style.background = i % 2 ? '#fcfcfd' : '#fff'; }}>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ background: c.statut === 'echec' ? '#ffebee'
                                       : recu ? '#e8f5e9' : '#e8f1ff',
                                     color: c.statut === 'echec' ? '#b71c1c'
                                       : recu ? '#1b5e20' : '#14448f',
                                     borderRadius: 999, padding: '3px 9px', fontSize: 11.5,
                                     fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {c.statut === 'echec' ? 'échec' : recu ? 'reçu' : 'envoyé'}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', color: '#546e7a', ...tronque }}>
                      {dateHeure(c.cree_le)}</td>
                    <td style={{ padding: '10px 12px', color: '#263238', ...tronque }}
                        title={recu ? c.de : c.destinataire}>
                      {recu ? c.de : c.destinataire}</td>
                    <td style={{ padding: '10px 12px', color: '#263238', ...tronque }} title={c.sujet}>
                      {c.sujet}</td>
                    <td style={{ padding: '10px 12px', color: '#546e7a', ...tronque }}>
                      {c.prenom ? `${c.prenom} ${c.nom_client || ''}` : '—'}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                      {recu ? <CornerUpLeft size={14} color="#3949ab" />
                            : <ChevronRight size={14} color="#b0bec5" />}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {donnees.total > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14,
                      flexWrap: 'wrap', fontSize: 13, color: '#546e7a' }}>
          <span>{nombre(donnees.total)} message{donnees.total > 1 ? 's' : ''} · page {donnees.page} sur {nombre(donnees.pages)}</span>
          <span style={{ flex: 1 }} />
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                  style={{ ...champ, background: '#fff', cursor: page <= 1 ? 'default' : 'pointer' }}>
            Précédent
          </button>
          <button onClick={() => setPage((p) => Math.min(donnees.pages, p + 1))}
                  disabled={page >= donnees.pages}
                  style={{ ...champ, background: '#fff',
                           cursor: page >= donnees.pages ? 'default' : 'pointer' }}>
            Suivant
          </button>
        </div>
      )}

      {ouvert && (
        <Message courriel={ouvert} onFermer={() => setOuvert(null)}
                 onRepondu={(t) => { setMessage({ type: 'ok', texte: t }); charger({ silencieux: true }); }} />
      )}
    </div>
  );
}
